import React, { useState, useMemo, useEffect } from "react";
import {
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Drawer,
  Stack,
  Typography,
  Card,
  FormControlLabel,
  Switch,
  useTheme,
} from "@mui/material";
import _ from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import ReactJson from "react-json-view";
import moment from "moment";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useDispatch, useSelector } from "react-redux";
import { fetchGPTUsageData, logSelector } from "../../../../store/slices/logs";
import AppConfig from "../../../../appConfig";

// LogDrawer Component
const LogDrawer = ({
  openDrawer,
  toggleDrawer,
  selectedReviewText,
  selectedReviewIds,
  showAll = false,
  isShowMaxRequestId = false,
  showAllLogs,
  setShowAllLogs,
  selectedCompany,
}) => {
  const theme = useTheme();
  // Redux setup
  const dispatch = useDispatch();
  const { gptUsage, input } = useSelector(logSelector);

  // Fetch GPT usage data on component mount and when dependencies change
  React.useEffect(() => {
    if (selectedCompany) {
      dispatch(
        fetchGPTUsageData(
          selectedCompany,
          showAllLogs
            ? null
            : localStorage.getItem("userId")
            ? localStorage.getItem("userId")
            : AppConfig.env("userId"),
          input
        )
      );
    }
  }, [selectedCompany, showAllLogs, input]);

  // Memoized sorted data
  const sortedData = useMemo(
    () => _.orderBy(gptUsage, ["createdOn"], ["asc"]),
    [gptUsage]
  );

  // Component state
  const [activeTab, setActiveTab] = useState("input");
  const [currentPage, setCurrentPage] = useState(0);

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Pagination constants
  const itemsPerPage = 1;

  // Memoized and filtered data based on selectedReviewIds and isShowMaxRequestId
  const filteredData = useMemo(() => {
    let filterData = sortedData;
    // Filter by Max RequestId if isShowMaxRequestId is true
    if (isShowMaxRequestId) {
      let maxRequestId =
        filterData?.length > 0
          ? JSON.parse(
              _.orderBy(filterData, ["createdOn"], ["desc"])?.[0]?.input
            )?.otherParameters?.RequestId
          : null;
      filterData = _.filter(filterData, (it) => {
        return (
          JSON.parse(it?.input)?.otherParameters?.RequestId === maxRequestId
        );
      });
    }

    // Filter by selectedReviewIds and selectedReviewText
    if (selectedReviewIds?.length > 0) {

      const filter = _.filter(filterData, (item) => {
        const inputJson = JSON.parse(item.input);
        const reviewId = inputJson?.['otherParameters']?.['ReviewId']
        const normalizedInput = item.input.replace(/\\n/g, "\n");
        const normalizedReviewText =
          typeof selectedReviewText === "string"
            ? selectedReviewText.replace(/\\n/g, "\n")
            : selectedReviewText;

        return selectedReviewIds.includes(reviewId);
      });
      return filter;
    } else {
      return filterData;
    }
    return filterData;
  }, [selectedReviewIds, selectedReviewText, sortedData]);

  // Total number of pages for pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Set current page to the last page when filtered data changes
  useEffect(() => {
    setCurrentPage(totalPages - 1);
  }, [filteredData]);

  // Utility function to remove newlines from a string recursively
  const removeNewlinesRecursive = (obj) => {
    if (typeof obj === "string") {
      return obj.replace(/\n/g, "");
    } else if (typeof obj === "object") {
      for (let key in obj) {
        obj[key] = removeNewlinesRecursive(obj[key]);
      }
    }
    return obj;
  };

  // Copy data to clipboard with removed newlines
  const handleCopy = (text) => {
    let data = {
      Input: JSON.parse(text?.input ?? ""),
      Output: JSON.parse(text?.output ?? ""),
    };
    // const modified = removeNewlinesRecursive(data);
    let final = JSON.stringify(data, undefined, 2);

    navigator.clipboard.writeText(final);
  };

  // LogDrawer UI
  return (
    <Drawer
      ModalProps={{ BackdropProps: { invisible: true } }}
      open={openDrawer}
      PaperProps={{
        sx: {
          top: 64,
          width: 670,
          height: "calc(100% - 64px)",
          backgroundColor: "white",
        },
      }}
      anchor="right"
      variant="persistent"
    >
      {/* Close button */}
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Tooltip title="Close">
          <IconButton
            onClick={() => {
              toggleDrawer(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Pagination controls */}
      <Stack direction="row" spacing={1} alignItems={"center"}>
        <IconButton
          sx={{ mr: 2 }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0 || filteredData.length === 0}
        >
          <ArrowLeftIcon />
        </IconButton>
        <div style={{ textAlign: "center" }}>
          {currentPage + 1} of {totalPages}
        </div>
        <IconButton
          size="small"
          sx={{ ml: 2 }}
          variant="outlined"
          color="primary"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={
            currentPage === Math.ceil(filteredData.length / itemsPerPage) - 1 ||
            filteredData.length === 0
          }
        >
          <ArrowRightIcon />
        </IconButton>
        {showAll && (
                    <FormControlLabel
                      control={
                        <Switch
                          value={showAllLogs}
                          onChange={(e) => setShowAllLogs(e.target.checked)}
                        />
                      }
                      label="Show All"
                    />
                  )}
      </Stack>

      {/* Log entries */}
      <div
        style={{
          padding: "16px",
        }}
      >
        {filteredData
          ?.filter((item, index) => {
            const startIndex = currentPage * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            return item[activeTab] && index >= startIndex && index < endIndex;
          })
          ?.map((item, index) => (
            <div key={index}>
              {/* Log entry details */}
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"left"}
                  spacing={1}
                  mx={-1}
                >
                  <AccessTimeIcon />
                  <Typography variant="subtitle1" gutterBottom>
                    {moment(moment.utc(item.createdOn).toDate()).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )}{" "}
                    by {item?.userName}
                  </Typography>
                 
                  {item?.output && (() => {
                    try {
                      const parsedOutput = JSON.parse(item.output);
                      return parsedOutput?.choices?.[0]?.message?.name === "Error";
                    } catch (error) {
                      return false;
                    }
                  })() && (
                      <Tooltip title="Error">
                        <ErrorIcon sx={{ fill: "red" }} />
                      </Tooltip>
                    )}
                </Stack>
                <Stack direction="row" spacing={2}>
                 
                  {/* Copy to Clipboard button */}
                  <Tooltip title="Copy to Clipboard">
                    <span>
                      <IconButton>
                        <FileCopyIcon
                          onClick={() => {
                            handleCopy(item);
                          }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Log entry content */}
              <Card>
                <div style={{ position: "relative" }}>
                  {/* Tabs for Input and Output */}
                  <Tabs
                    sx={{ borderBottom: "1px solid #888" }}
                    value={activeTab}
                    onChange={handleTabChange}
                  >
                    <Tab label="Input" value="input" />
                    <Tab label="Output" value="output" />
                  </Tabs>
                </div>
                {/* JSON view of Input/Output data */}
                <div
                  style={{
                    overflowY: "auto",
                    height: "calc(80vh - 110px)",
                    padding: "16px",
                  }}
                >
                  <ReactJson
                    style={{ marginTop: "10px", whiteSpace: "pre-wrap" }}
                    src={(() => {
                      try {
                        return JSON.parse(item[activeTab]);
                      } catch (error) {
                        return item;
                      }
                    })()}
                    name={null}
                    theme={theme.palette.mode === 'dark' ? 'pop' : 'rjv-default'}
                    collapsed={false}
                    
                    displayDataTypes={false}
                  />
                </div>
              </Card>
            </div>
          ))}
      </div>
    </Drawer>
  );
};

export default LogDrawer;
